import { useEffect, useMemo, useState } from 'react';
import { Button, NewLoading, SwipeableCustomerDrawer, CustomerDrawer, Typography, KImage } from '@qlub-dev/qlub-kit';
import { Box, IconButton } from '@mui/material';
import classNames from 'classnames';
import { KeyboardArrowLeftRounded } from '@mui/icons-material';
import { useQlubRouter } from '../../hook/router';
import { IDrawerConstants } from '../../context/auth';
import { ILoginCallbacks, ILoginOptions } from '../../context/auth/types';
import { IProfile } from '../../repository/auth/type';
import SplashLegacy from '../SplashLegacy';
import { dataManager } from './DataManager/dataManager';
import { useTranslation } from '../../hook/translations';
import Phone from './Phone';
import AdditionalPhonePhaseElements from './AdditionalPhonePhaseElements';
import OTP from './OTP';
import { profileEventsCenter } from '../../hook/eventsCenter';
import { ConsentType } from '../Consent';
import UserConsent from '../Consent/UserConsent';

import styles from './index.module.scss';
import { themeOptions } from '../../context/config';

export interface IProps {
    getRecaptchaToken: (reserve?: boolean) => Promise<string | null>;
    setOpenFn: (fn: (options?: ILoginOptions) => Promise<any>) => void;
    drawerConstants: IDrawerConstants | undefined;
    fullPage?: boolean;
    withSSOLogin?: boolean;
    onLogin: (login: boolean, profile?: IProfile) => void;
    loginCallbacks: ILoginCallbacks;
    loginWidgets: React.ReactNode | null;
    loginVendorLogo: string | null;
}

function Login({
    getRecaptchaToken,
    setOpenFn,
    drawerConstants,
    onLogin,
    fullPage,
    withSSOLogin,
    loginCallbacks,
    loginWidgets,
    loginVendorLogo,
}: IProps) {
    const { t } = useTranslation('common');
    const { url, lang } = useQlubRouter();

    const termsAndConditions = drawerConstants?.termsAndConditions;
    const [acceptTermsAndCondition, setAcceptTermsAndCondition] = useState(termsAndConditions?.defaultChecked);

    const {
        data: {
            appleLoading,
            phoneErr,
            optErr,
            loading,
            open,
            step,
            phone,
            smsRes,
            code,
            resendTimer,
            validPhone,
            hasConsented,
        },
        action: {
            submitHandler,
            resetHandler,
            loginHandler,
            resendSmsHandler,
            modalCloseHandler,
            backHandler,
            changePhoneHandler,
            setCodeHandler,
            handleAppleLogin,
            handleGoogleLoginInit,
            phoneValidationHandler,
            setHasConsented,
        },
    } = dataManager({ getRecaptchaToken, setOpenFn, onLogin, loginCallbacks });

    const {
        userProfile: { submitOTPEvent },
    } = profileEventsCenter();

    useEffect(() => {
        setAcceptTermsAndCondition(termsAndConditions?.defaultChecked);
    }, [termsAndConditions]);

    const content = useMemo(() => {
        switch (step) {
            case 'phone':
                return (
                    <div>
                        <Phone
                            lang={lang}
                            defaultCountry={url.cc}
                            value={phone}
                            onChange={changePhoneHandler}
                            errorText={phoneErr || t('Please enter a valid mobile number')}
                            showTermsAndCondition={termsAndConditions?.show}
                            acceptTermsAndCondition={acceptTermsAndCondition}
                            termsAndConditionLabel={termsAndConditions?.label}
                            termsAndConditionContent={termsAndConditions?.content}
                            onChangeAcceptTermsAndCondition={setAcceptTermsAndCondition}
                            validation={{
                                enable: true,
                                callbackFn: phoneValidationHandler,
                            }}
                        />
                    </div>
                );
            case 'otp':
                return (
                    <div className={styles.otpContainer}>
                        <OTP
                            numInputs={drawerConstants?.numInputs}
                            code={code}
                            onSetCode={setCodeHandler}
                            goBackClick={backHandler}
                            showResendCode={Boolean(smsRes)}
                            resendSms={() => resendSmsHandler()}
                            resendSmsCooldown={30}
                            backButton
                            fullPage={fullPage}
                            resendTimer={resendTimer}
                            error={optErr}
                        />
                    </div>
                );
            case 'done':
                return (
                    <Box
                        sx={{
                            minHeight: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <NewLoading color="containedDestructive" />
                    </Box>
                );
            default:
                return null;
        }
    }, [step, code, url.cc, lang, smsRes, fullPage, changePhoneHandler, resendSmsHandler, loginHandler]);

    const buttonContent = useMemo(() => {
        switch (step) {
            case 'phone':
                return (
                    <>
                        <UserConsent
                            consentBoxProps={{
                                hasConsented,
                                handleClick: () => setHasConsented((prev) => !prev),
                            }}
                            messageFactoryProps={{
                                type: ConsentType.Privacy,
                                messageProps: {
                                    modalCloseHandler: () => modalCloseHandler(),
                                },
                            }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={
                                !hasConsented ||
                                !validPhone ||
                                loading ||
                                (termsAndConditions?.show && !acceptTermsAndCondition)
                            }
                            onClick={() => {
                                loginHandler();
                            }}
                            sx={fullPage ? { mt: 2, mb: 3 } : {}}
                        >
                            {loading ? <NewLoading /> : drawerConstants?.text.phone.buttonText || t('Next')}
                        </Button>
                    </>
                );
            case 'otp':
                return (
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={loading || code.length !== drawerConstants?.numInputs}
                        loading={loading}
                        onClick={() => {
                            submitOTPEvent();
                            submitHandler();
                        }}
                        className={fullPage ? styles.otpFixedButton : ''}
                    >
                        {loading ? <NewLoading /> : drawerConstants?.text.otp.buttonText || t('Submit')}
                    </Button>
                );
            default:
                return null;
        }
    }, [
        step,
        loading,
        loginHandler,
        code,
        drawerConstants,
        fullPage,
        acceptTermsAndCondition,
        termsAndConditions,
        hasConsented,
    ]);

    const headerContent = useMemo(() => {
        switch (step) {
            case 'phone':
                return (
                    <Typography typo="title_md" sx={{ pt: 2, pb: fullPage ? 2 : 0 }}>
                        {t('Enter your mobile number to login or register')}
                    </Typography>
                );
            case 'otp':
                return (
                    <>
                        <Typography typo="title_md" sx={{ pt: 2 }}>
                            {t('Enter 5-digit code')}
                        </Typography>
                        <Typography className={styles.otpCaption} sx={{ pt: 1, pb: 2 }} typo="caption_overline">
                            {`${t('The 5-digit code is sent to')}`}
                            <span className={styles.number}>{phone}</span>
                        </Typography>
                    </>
                );
            default:
                return null;
        }
    }, [phone, step, fullPage]);

    const additionalContent = useMemo(() => {
        switch (step) {
            case 'phone':
                return (
                    <AdditionalPhonePhaseElements
                        onModalClose={modalCloseHandler}
                        fullPage={fullPage}
                        handleAppleLogin={handleAppleLogin}
                        handleGoogleLoginInit={handleGoogleLoginInit}
                        withSSOLogin={withSSOLogin}
                    />
                );
            default:
                return null;
        }
    }, [step, url, lang]);

    const getTexts = useMemo(() => {
        return drawerConstants?.text?.[step];
    }, [t]);

    if (appleLoading) {
        return <SplashLegacy hideIcon={themeOptions.hideLogo} />;
    }

    if (fullPage) {
        return (
            <CustomerDrawer
                headerTitle={getTexts?.title || t('Login to qlub')}
                anchor="bottom"
                open={open}
                contentStyle={{
                    display: 'flex',
                    gap: '32px',
                    flexDirection: 'column',
                    padding: '0 1.5rem',
                    height: '100%',
                }}
                onClose={modalCloseHandler}
                BackdropProps={{ sx: { background: '#00000099' } }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        background: 'white',
                    },
                }}
                removeHeader
                className={classNames(styles.outerContainer)}
            >
                <div className={classNames(styles.login, { [styles.dynamicHeight]: step === 'otp' })}>
                    <div className={styles.header}>
                        <IconButton className={classNames(styles.backButton)} onClick={backHandler}>
                            <KeyboardArrowLeftRounded />
                        </IconButton>
                        {loginVendorLogo && (
                            <div className={styles.loginDrawerHeader}>
                                <KImage src={loginVendorLogo} alt="vendor logo" maxSize={28} />
                            </div>
                        )}
                    </div>
                    {step === 'phone' && loginWidgets}
                    {headerContent}
                    <div className={styles.content}>{content}</div>
                    <div>{buttonContent}</div>
                    {additionalContent}
                </div>
            </CustomerDrawer>
        );
    }

    return (
        <SwipeableCustomerDrawer
            onOpen={() => {
                resetHandler();
            }}
            caption={getTexts?.description || t('Enter your mobile number to login or register')}
            headerTitle={getTexts?.title || t('Login')}
            anchor="bottom"
            open={open}
            contentStyle={{
                display: 'flex',
                gap: '32px',
                flexDirection: 'column',
                padding: '0 18px',
            }}
            onClose={modalCloseHandler}
            BackdropProps={{ sx: { background: '#00000099' } }}
            PaperProps={{
                elevation: 0,
                sx: {
                    background: 'white',
                    borderRadius: ' 30px 30px 0 0',
                },
            }}
        >
            <div className={styles.login}>
                <div className={`${styles.content} ${styles.modal}`}>{content}</div>
                <div className={styles.buttonWrapper}>{buttonContent}</div>
            </div>
        </SwipeableCustomerDrawer>
    );
}

export default Login;
